import React, {
  useState,
  useCallback,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { useDropzone } from 'react-dropzone';
import Cropper from 'react-easy-crop';
import Slider from '@mui/material/Slider';
import { ImageCropWrapper } from './Component.style';
import { ReactComponent as ZoomIn } from 'assert/images/Common/zoomIn.svg';
import { ReactComponent as ZoomOut } from 'assert/images/Common/zoomOut.svg';
import { ReactComponent as UploadIcon } from 'assert/images/Jobs/upload.svg';
import { ReactComponent as DeleteIcon } from 'assert/images/More/delete.svg';
import { ReactComponent as BackArrow } from 'assert/images/Common/backArrow.svg';
import clsx from 'clsx';

const ImageCrop = forwardRef(({ profileImage, handleSetImage, handleDeleteImg }, ref) => {
  const [image, setImage] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    if (file.size / 1024 ** 2 > 8) {
      alert('Image size should not exceed 8 MB');
      return;
    }

    const preview = URL.createObjectURL(file);
    setImage(preview);
    handleSetImage(preview);
  }, []);

  const { getRootProps, getInputProps, open } = useDropzone({
    onDrop,
    accept: { 'image/*': [] },
    multiple: false,
    maxFiles: 1,
    noClick: true,
    noKeyboard: true,
  });

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  useEffect(() => {
    return () => {
      if (image) URL.revokeObjectURL(image);
    };
  }, [image]);

  const getCroppedImg = useCallback(() => {
    return new Promise((resolve, reject) => {
      if (!image || !croppedAreaPixels) return resolve(null);

      const img = new Image();
      img.src = image;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = croppedAreaPixels.width;
        canvas.height = croppedAreaPixels.height;
        const ctx = canvas.getContext('2d');

        ctx.drawImage(
          img,
          croppedAreaPixels.x,
          croppedAreaPixels.y,
          croppedAreaPixels.width,
          croppedAreaPixels.height,
          0,
          0,
          croppedAreaPixels.width,
          croppedAreaPixels.height
        );

        canvas.toBlob((blob) => {
          if (!blob) return reject(new Error('Empty blob'));
          const url = URL.createObjectURL(blob);
          resolve({ blob, url });
        }, 'image/jpeg');
      };

      img.onerror = reject;
    });
  }, [image, croppedAreaPixels]);

  useImperativeHandle(ref, () => ({
    async getCroppedImage() {
      return await getCroppedImg();
    },
  }));

  return (
    <ImageCropWrapper>
      <div {...getRootProps()} className={clsx(image && 'cropper-container')}>
        <input {...getInputProps()} />

        {!image ? (
          <div className="circle-uploader-container">
            <div className="circle-uploader" onClick={open}>
              {profileImage?.length > 0 ? (
                <img
                  src={profileImage}
                  alt="Profile"
                  className="preview-image"
                  style={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    objectFit: 'cover',
                  }}
                />
              ) : (
                <div className='circle-upload-block'>
                  <UploadIcon />
                  <div>Drag or Upload</div>
                </div>
              )}
            </div>

            {profileImage?.length > 0 && (
              <div className="profile-icon-actions">
                <UploadIcon
                  className="icon-button icon-upload"
                  title="Replace"
                  onClick={open}
                />
                <DeleteIcon
                  className="icon-button icon-delete"
                  title="Delete"
                  onClick={() => {
                    setImage(null);
                    handleDeleteImg();
                    handleSetImage(null);
                  }}
                />
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="cropper-wrapper">
              <Cropper
                image={image}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                showGrid={false}
                onCropChange={setCrop}
                onCropComplete={onCropComplete}
                onZoomChange={setZoom}
              />
            </div>

            <div className="zoom-slider">
              <button
                type="button"
                onClick={() => setZoom((z) => Math.max(1, z - 0.1))}
              >
                <ZoomOut />
              </button>
              <Slider
                min={1}
                max={3}
                step={0.01}
                value={zoom}
                onChange={(_, value) => setZoom(value)}
                aria-labelledby="Zoom"
              />
              <button
                type="button"
                onClick={() => setZoom((z) => Math.min(3, z + 0.1))}
              >
                <ZoomIn />
              </button>
            </div>

            <BackArrow
              className="icon-button icon-back"
              title="Back"
              onClick={() => {
                setImage(null);
                handleSetImage(null);
              }}
            />

            <div className="icon-actions">
              <UploadIcon
                className="icon-button icon-upload"
                title="Replace"
                onClick={open}
              />
            </div>
          </>
        )}
      </div>
    </ImageCropWrapper>
  );
});

ImageCrop.displayName = 'ImageCrop';

export default ImageCrop;
